/**
 * Timeline Hooks
 * 
 * React hooks for timeline and event operations
 */

import { useCallback, useEffect, useState } from 'react';

interface TimelineEvent {
    id: string;
    timestamp: string;
    event_type: string;
    actor: string;
    action: string;
    target: string;
    result: 'success' | 'failure' | 'warning' | 'info';
    duration_ms: number;
    parent_event_id?: string;
    child_events?: string[];
    metadata: Record<string, any>;
    context?: Record<string, any>;
    severity: 'low' | 'medium' | 'high' | 'critical';
}

interface TimelineAnalytics {
    total_events: number;
    error_rate: number;
    peak_hour: string;
    avg_duration: number;
    event_types_breakdown: Record<string, number>;
    actors_activity: Record<string, number>;
}

// Generate mock timeline events
const generateMockTimelineEvents = (count: number = 50): TimelineEvent[] => {
    const eventTypes = ['state_transition', 'signal_received', 'action_executed', 'error_occurred', 'user_interaction'];
    const actors = ['system', 'user_alice', 'user_bob', 'scheduler', 'api_service'];
    const actions = ['validate_input', 'process_data', 'send_notification', 'update_database', 'generate_report'];
    const targets = ['workflow_instance', 'user_profile', 'notification_service', 'database_record', 'external_api'];
    const results: ('success' | 'failure' | 'warning' | 'info')[] = ['success', 'failure', 'warning', 'info'];
    const severities: ('low' | 'medium' | 'high' | 'critical')[] = ['low', 'medium', 'high', 'critical'];

    const events: TimelineEvent[] = [];
    const now = new Date();

    for (let i = 0; i < count; i++) {
        const timestamp = new Date(now.getTime() - (i * 30000 + Math.random() * 60000)); // Events spread over time

        const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)] ?? 'state_transition';
        const actor = actors[Math.floor(Math.random() * actors.length)] ?? 'system';
        const action = actions[Math.floor(Math.random() * actions.length)] ?? 'validate_input';
        const target = targets[Math.floor(Math.random() * targets.length)] ?? 'workflow_instance';
        const result = results[Math.floor(Math.random() * results.length)] ?? 'success';
        const severity = severities[Math.floor(Math.random() * severities.length)] ?? 'low';

        events.push({
            id: `event_${i.toString().padStart(3, '0')}`,
            timestamp: timestamp.toISOString(),
            event_type: eventType,
            actor,
            action,
            target,
            result,
            duration_ms: Math.floor(Math.random() * 5000) + 100,
            parent_event_id: i > 0 && Math.random() < 0.2 ? `event_${Math.floor(i / 2).toString().padStart(3, '0')}` : undefined,
            metadata: {
                correlation_id: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
                trace_id: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
                source: 'workflow_engine',
                version: '1.0.0'
            },
            context: {
                workflow_id: 'wf_001',
                instance_id: 'inst_001',
                step_id: `step_${i % 5 + 1}`
            },
            severity
        });
    }

    return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

// Generate mock analytics
const generateMockAnalytics = (events: TimelineEvent[]): TimelineAnalytics => {
    const total_events = events.length;
    const failed_events = events.filter(e => e.result === 'failure').length;
    const error_rate = total_events > 0 ? (failed_events / total_events) * 100 : 0;
    
    const hourly_counts = events.reduce((acc, event) => {
        const hour = new Date(event.timestamp).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
    }, {} as Record<number, number>);

    const peak_hour = Object.entries(hourly_counts)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || '0';

    const avg_duration = total_events > 0 
        ? Math.floor(events.reduce((acc, e) => acc + e.duration_ms, 0) / total_events)
        : 0;

    const event_types_breakdown = events.reduce((acc, event) => {
        acc[event.event_type] = (acc[event.event_type] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const actors_activity = events.reduce((acc, event) => {
        acc[event.actor] = (acc[event.actor] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    return {
        total_events,
        error_rate,
        peak_hour: `${peak_hour}:00`,
        avg_duration,
        event_types_breakdown,
        actors_activity
    };
};

// Hook for fetching timeline events
export function useTimelineEvents(options?: {
    instanceId?: string;
    workflowId?: string;
    actorFilter?: string;
    timeRange?: 'hour' | 'day' | 'week' | 'month';
    limit?: number;
    offset?: number;
}) {
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Generate mock data based on options
            let mockEvents = generateMockTimelineEvents(options?.limit || 50);

            // Apply filters
            if (options?.actorFilter) {
                mockEvents = mockEvents.filter(event => event.actor === options.actorFilter);
            }

            if (options?.instanceId) {
                mockEvents = mockEvents.filter(event => 
                    event.context?.instance_id === options.instanceId
                );
            }

            if (options?.workflowId) {
                mockEvents = mockEvents.filter(event => 
                    event.context?.workflow_id === options.workflowId
                );
            }

            // Apply time range filter
            if (options?.timeRange) {
                const now = new Date();
                const timeRanges = {
                    hour: 60 * 60 * 1000,
                    day: 24 * 60 * 60 * 1000,
                    week: 7 * 24 * 60 * 60 * 1000,
                    month: 30 * 24 * 60 * 60 * 1000
                };

                const cutoff = new Date(now.getTime() - timeRanges[options.timeRange]);
                mockEvents = mockEvents.filter(event => 
                    new Date(event.timestamp) >= cutoff
                );
            }

            setEvents(mockEvents);
        } catch (err) {
            setError(err as Error);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [options]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    return {
        events,
        loading,
        error,
        refetch: fetchEvents
    };
}

// Hook for timeline analytics
export function useTimelineAnalytics(options?: {
    instanceId?: string;
    workflowId?: string;
    timeRange?: 'hour' | 'day' | 'week' | 'month';
}) {
    const [analytics, setAnalytics] = useState<TimelineAnalytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 800));

            // Generate mock events for analytics
            const mockEvents = generateMockTimelineEvents(100);
            const mockAnalytics = generateMockAnalytics(mockEvents);

            setAnalytics(mockAnalytics);
        } catch (err) {
            setError(err as Error);
            setAnalytics(null);
        } finally {
            setLoading(false);
        }
    }, [options]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    return {
        analytics,
        loading,
        error,
        refetch: fetchAnalytics
    };
}

// Hook for single timeline event
export function useTimelineEvent(eventId: string | null) {
    const [event, setEvent] = useState<TimelineEvent | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchEvent = useCallback(async () => {
        if (!eventId) {
            setEvent(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 500));

            // Generate a mock event
            const mockEvent: TimelineEvent = {
                id: eventId,
                timestamp: new Date().toISOString(),
                event_type: 'state_transition',
                actor: 'system',
                action: 'validate_input',
                target: 'workflow_instance',
                result: 'success',
                duration_ms: 250,
                metadata: {
                    correlation_id: 'corr_abc123',
                    trace_id: 'trace_def456',
                    source: 'workflow_engine',
                    version: '1.0.0'
                },
                context: {
                    workflow_id: 'wf_001' as string,
                    instance_id: 'inst_001' as string,
                    step_id: 'step_1' as string
                },
                severity: 'low'
            };

            setEvent(mockEvent);
        } catch (err) {
            setError(err as Error);
            setEvent(null);
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        fetchEvent();
    }, [fetchEvent]);

    return {
        event,
        loading,
        error,
        refetch: fetchEvent
    };
}